import { ReactNode, RefObject, useMemo } from "react";
import { useNavigation } from "react-router-dom";

import { IgLinearProgress } from "@intelligems/ig-theme/IgLinearProgress";
import { Box, Grid, styled, SxProps, useTheme } from "@mui/material";
import { useAlertBannerHeight } from "@src/providers/layout/useAlertBanner";
import { useMeasuredElementHeight } from "@src/providers/layout/useMeasuredElementHeight";
import {
  PAGE_CONTENT_PADDING_X_VAR,
  PAGE_CONTENT_TOP_OFFSET_VAR,
} from "@src/utils/pageContentLayout";
import { IgPageContentTopBar } from "@src/utils/IgPageContentTopBar";

const Root = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  paddingTop: theme.spacing(4),
  backgroundColor: theme.igBackgroundColors.baseLightMid,
  boxSizing: "border-box",
}));

interface IgPageContentProps {
  children: ReactNode;
  childrenSx?: SxProps;
  paddingX?: number;
  igPageContentRef?: RefObject<null>;
}

export const IgPageContent = (props: IgPageContentProps) => {
  const { state } = useNavigation();
  const { children, igPageContentRef, childrenSx, paddingX, ...rest } = props;
  const isLoadingRoute = state === "loading";
  const alertBannerHeight = useAlertBannerHeight();
  const theme = useTheme();
  const contentPaddingX = paddingX ?? 10;
  const { ref: topBarRef, heightPx: topBarHeight } =
    useMeasuredElementHeight<HTMLDivElement>();

  return (
    <Root
      id={"ig-page-content"}
      sx={{
        position: "relative",
        // Fill the viewport below the app's alert banner, which sits above
        // this element, so a short page never scrolls by the banner's height.
        minHeight: `calc(100svh - ${alertBannerHeight}px)`,
        // Top padding + bar + the column's row gap, both theme.spacing(4).
        [PAGE_CONTENT_TOP_OFFSET_VAR]: `calc(${theme.spacing(8)} + ${topBarHeight}px)`,
        [PAGE_CONTENT_PADDING_X_VAR]: theme.spacing(contentPaddingX),
      }}
      {...rest}
    >
      {isLoadingRoute && (
        <IgLinearProgress
          sx={{ position: "absolute", top: 0, left: 0, width: "100%" }}
        />
      )}

      <Grid
        rowSpacing={4}
        sx={{
          height: "100%",
          flexWrap: "nowrap",
          flex: 1,
          paddingX: contentPaddingX,
        }}
        container
        direction={"column"}
      >
        <Box ref={topBarRef} sx={{ flexShrink: 0, width: "100%" }}>
          <IgPageContentTopBar />
        </Box>

        <Grid
          ref={igPageContentRef}
          id={"ig-page-content-scrollable"}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            ...childrenSx,
          }}
        >
          {children}
        </Grid>
      </Grid>
    </Root>
  );
};
